import {
  ActionDropdown,
  ActionDropdownContent,
  ActionDropdownTrigger,
} from "@/components/ActionDropdown";
import { ResumeType } from "@/services/profile.service";
import { ColumnDef } from "@tanstack/react-table";
import { FileText, MoreHorizontal, Star, Trash2 } from "lucide-react";
import { format } from "date-fns";

export type ResumeActionType = {
  action: "set_primary" | "delete";
  id: string;
};

export const createResumeColumns = (
  onActionClick: (payload: ResumeActionType) => void
): ColumnDef<ResumeType>[] => [
  {
    accessorKey: "file_url",
    header: "Resume File",
    cell: ({ row }) => (
      <a 
        href={row.original.file_url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium max-w-xs truncate"
      >
        <FileText size={16} className="shrink-0" />
        <span className="truncate">View Document</span>
      </a>
    ),
  },
  {
    accessorKey: "version",
    header: "Version",
    cell: ({ row }) => <span>v{row.original.version}</span>,
  },
  {
    accessorKey: "is_primary",
    header: "Status",
    cell: ({ row }) =>
      row.original.is_primary ? (
        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1 w-max">
          <Star size={12} className="fill-emerald-700" /> Primary
        </span>
      ) : (
        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
          Alternative
        </span>
      ),
  },
  {
    accessorKey: "uploaded_at",
    header: "Uploaded On",
    cell: ({ row }) => {
      const date = row.original.uploaded_at;
      return date ? format(new Date(date), "dd MMM yyyy, p") : "-";
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const resume = row.original;

      return (
        <ActionDropdown>
          <ActionDropdownTrigger
            triggerContent={<MoreHorizontal className="h-4 w-4" />}
            buttonVariant="ghost"
            ariaLabel="Open menu"
            className={{ buttonClass: "h-8 w-8 p-0" }}
          />
          <ActionDropdownContent
            align="end"
            dropdownShape={[
              ...(!resume.is_primary
                ? [
                    {
                      type: "item" as const,
                      label: "Set as Primary",
                      icon: <Star size={16} />,
                      className: "cursor-pointer gap-2 text-indigo-600 focus:text-indigo-700",
                      onSelect: () => onActionClick({ action: "set_primary", id: resume.id }),
                    },
                  ]
                : []),
              {
                type: "item" as const,
                label: "Delete Resume",
                icon: <Trash2 size={16} />,
                className: "cursor-pointer gap-2 text-red-600 focus:text-red-700",
                onSelect: () => onActionClick({ action: "delete", id: resume.id }),
              },
            ]}
          />
        </ActionDropdown>
      );
    },
  },
];
